import { useEffect, useState } from 'react';
import {
  Drawer,
  Skeleton,
  Empty,
  Button,
  Space,
  Tooltip,
  Typography,
  Tag,
} from 'antd';
import {
  ToolOutlined,
  CopyOutlined,
  ExportOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  AppstoreOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { getSkillById } from '@/api/skills';
import type { AiSkill } from '@/api/types';
import { colors } from '@/theme/tokens';
import { useStore } from '@/stores';
import { StatusPill } from '@/components/StatusPill';
import 'highlight.js/styles/github.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// SkillContentDrawer — in-place reader for an agent's linked skill
// ─────────────────────────────────────────────────────────────────────────────
//
// PURPOSE
//   The agent's Skills tab shows a row per skill (name, description,
//   category, always-load, version). Clicking a row used to navigate
//   away to /skills/{id} — that's a context switch that breaks the
//   nurse's flow. This drawer surfaces the full skill content
//   (Description + the long-form Instructions body, rendered as
//   markdown) without leaving the agent page.
//
// LOAD STRATEGY
//   The Skills tab pulls a list view that omits `instructions` (the
//   long body) for bandwidth. When the user opens the drawer, we
//   fetch the full skill via /api/AiSkill/GetById/{id} which includes
//   the body. Cached via in-component state — close + reopen on the
//   same skill skips the round trip.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  skillId: number | null;
  /** Optional — populates the header instantly while the body loads. */
  preview?: AiSkill | null;
  onClose: () => void;
  /**
   * Optional — when provided, the drawer renders a "View history" button
   * that closes this drawer and asks the host to open the SkillHistoryDrawer
   * for the same skill. The host owns both drawers because they should be
   * mutually exclusive (only one is visible at a time).
   */
  onOpenHistory?: (skill: AiSkill) => void;
}

export function SkillContentDrawer({ skillId, preview, onClose, onOpenHistory }: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [skill, setSkill] = useState<AiSkill | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (skillId == null) {
      setSkill(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const full = await getSkillById(skillId);
        if (!cancelled) {
          setSkill(full);
          setLoading(false);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[SkillContentDrawer] fetch failed:', err);
        if (!cancelled) {
          setError(
            'We could not load this skill. Try the reload button, or contact support if it keeps happening.',
          );
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skillId]);

  const reload = () => {
    if (skillId == null) return;
    setLoading(true);
    setError(null);
    void getSkillById(skillId).then(
      (s) => {
        setSkill(s);
        setLoading(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('[SkillContentDrawer] reload failed:', err);
        setError('Reload failed. Try again.');
        setLoading(false);
      },
    );
  };

  // Header label sources: prefer the freshly fetched skill, fall back to
  // the row preview so the title is non-empty during the fetch.
  const headerSkill = skill ?? preview ?? null;

  return (
    <Drawer
      open={skillId != null}
      onClose={onClose}
      width="90%"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <ToolOutlined style={{ color: colors.violet[600], fontSize: 18 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {headerSkill?.name ?? 'Skill'}
            </div>
            {headerSkill?.description && (
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '60vw',
                }}
              >
                {headerSkill.description}
              </Text>
            )}
          </div>
          {headerSkill?.category && (
            <StatusPill tone="ink">{headerSkill.category}</StatusPill>
          )}
          {headerSkill?.alwaysLoad ? (
            <StatusPill tone="green" icon={<CheckCircleOutlined />}>
              Always-load
            </StatusPill>
          ) : (
            <StatusPill tone="slate">On demand</StatusPill>
          )}
          {headerSkill?.version && (
            <Tag
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                margin: 0,
              }}
            >
              v{headerSkill.version}
            </Tag>
          )}
        </div>
      }
      extra={
        <Space size="small">
          <Tooltip title="Reload from server">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={reload}
              disabled={loading || skillId == null}
            />
          </Tooltip>
          {onOpenHistory && headerSkill && (
            <Tooltip title="View edit history and compare versions">
              <Button
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => {
                  onOpenHistory(headerSkill);
                  onClose();
                }}
              >
                History
              </Button>
            </Tooltip>
          )}
          {skillId != null && (
            <Link to={`/skills/${skillId}`} target="_blank" rel="noopener noreferrer">
              <Tooltip title="Open the skill detail page in a new tab">
                <Button size="small" icon={<ExportOutlined />}>
                  Open detail page
                </Button>
              </Tooltip>
            </Link>
          )}
        </Space>
      }
      styles={{
        body: { padding: 0, background: isDark ? colors.slate[900] : '#FFFFFF' },
      }}
      destroyOnClose
    >
      {loading && !skill ? (
        <div style={{ padding: 24 }}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : error ? (
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: isDark ? colors.slate[300] : colors.slate[600], fontSize: 13 }}>
                {error}
              </span>
            }
          >
            <Button onClick={reload}>Try again</Button>
          </Empty>
        </div>
      ) : skill ? (
        <div
          style={{
            height: 'calc(100vh - 120px)',
            overflow: 'auto',
          }}
        >
          <div
            style={{
              maxWidth: 880,
              margin: '0 auto',
              padding: '32px 40px 80px',
              color: isDark ? colors.slate[100] : colors.slate[800],
              fontSize: 15,
              lineHeight: 1.7,
            }}
          >
            {/* Description block — pulled out of the markdown body because
                it has its own UI affordance (italic, distinct color) and
                we want it visible regardless of whether instructions are
                present. */}
            {skill.description && (
              <div
                style={{
                  fontSize: 14,
                  fontStyle: 'italic',
                  color: isDark ? colors.slate[300] : colors.slate[600],
                  borderLeft: `3px solid ${colors.violet[600]}`,
                  paddingLeft: 14,
                  marginBottom: 28,
                  lineHeight: 1.6,
                }}
              >
                {skill.description}
              </div>
            )}

            {/* Section header for the long-form body. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 14,
              }}
            >
              <h2
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: isDark ? colors.slate[200] : colors.slate[700],
                }}
              >
                <AppstoreOutlined /> Instructions
              </h2>
              {skill.instructions && (
                <Tooltip title="Copy raw markdown to clipboard">
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      void navigator.clipboard?.writeText(skill.instructions ?? '');
                      setCopied(true);
                      window.setTimeout(() => setCopied(false), 1500);
                    }}
                  >
                    {copied ? 'Copied' : 'Copy raw'}
                  </Button>
                </Tooltip>
              )}
            </div>

            {!skill.instructions ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ fontSize: 13, color: isDark ? colors.slate[400] : colors.slate[500] }}>
                    No instructions on file for this skill.
                  </span>
                }
              />
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeHighlight]}
                components={{
                  h1: ({ children }) => (
                    <h1 style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 14, lineHeight: 1.25 }}>
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 style={{ fontSize: 20, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginTop: 20, marginBottom: 10 }}>
                      {children}
                    </h3>
                  ),
                  table: ({ children }) => (
                    <div style={{ overflowX: 'auto', margin: '14px 0' }}>
                      <table
                        style={{
                          borderCollapse: 'collapse',
                          width: '100%',
                          fontSize: 14,
                        }}
                      >
                        {children}
                      </table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th
                      style={{
                        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                        background: isDark ? colors.slate[800] : colors.slate[50],
                        padding: '8px 12px',
                        textAlign: 'left',
                        fontWeight: 600,
                      }}
                    >
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td
                      style={{
                        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                        padding: '8px 12px',
                        verticalAlign: 'top',
                      }}
                    >
                      {children}
                    </td>
                  ),
                  code: ({ className, children, ...props }) => {
                    const isBlock = (className ?? '').startsWith('language-');
                    if (isBlock) {
                      return (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                    return (
                      <code
                        style={{
                          background: isDark ? colors.slate[800] : colors.slate[100],
                          color: isDark ? colors.violet[100] : colors.violet[700],
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontSize: '0.92em',
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      >
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre
                      style={{
                        background: isDark ? colors.slate[900] : '#F8FAFC',
                        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                        borderRadius: 8,
                        padding: '12px 14px',
                        overflow: 'auto',
                        fontSize: 13,
                        lineHeight: 1.55,
                        margin: '14px 0',
                      }}
                    >
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote
                      style={{
                        borderLeft: `3px solid ${colors.violet[600]}`,
                        background: isDark ? `${colors.violet[700]}15` : colors.violet[50],
                        padding: '6px 14px',
                        margin: '14px 0',
                        color: isDark ? colors.slate[200] : colors.slate[700],
                        borderRadius: '0 6px 6px 0',
                      }}
                    >
                      {children}
                    </blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: colors.violet[600], textDecoration: 'underline' }}
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {skill.instructions}
              </ReactMarkdown>
            )}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
